// useBrandAssets — the Assets → Brand data hooks: branding (logos + colours), the 5 photo
// slots, bios, and the shared version history + restore. Every read forces the tenant key ['band', bandId, …]
// via useBandQuery; every write invalidates the matching key(s) on success. All gated assets:write server-side
// (the screen also cues with useCapability). Logo/photo BYTES go through useMedia (the flow); these hooks
// only carry the resource DTOs (the referenced media ids + resolved URLs).
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type BrandingRead = components["schemas"]["BrandingRead"];
export type UpdateBrandingRequest = components["schemas"]["UpdateBrandingRequest"];
export type BandPhotosRead = components["schemas"]["BandPhotosRead"];
export type BandPhotoView = components["schemas"]["BandPhotoView"];
export type PhotoSlotInput = components["schemas"]["PhotoSlotInput"];
export type BioRead = components["schemas"]["BioRead"];
export type BrandVersion = components["schemas"]["BrandVersion"];

/** The brand_snapshots scopes the version UI drives (epk added by).*/

// ---- branding --------------------------------------------------------------------------------------------------

// The three editor reads carry LOCAL DRAFT state in the screen (keyed to remount on a deliberate save/restore
// invalidation), so a BACKGROUND refetch must never clobber an unsaved edit: staleTime:Infinity +
// refetchOnWindowFocus/Reconnect:false override the app-client defaults (refetchOnReconnect:"always",
// refetchOnWindowFocus:true) so dataUpdatedAt only moves on the initial load, an explicit Retry, or the
// save/restore invalidateQueries (which force a refetch regardless of staleTime). The read-only versions query
// keeps the app defaults (no draft to lose).
const EDITOR_READ = { staleTime: Infinity, refetchOnWindowFocus: false, refetchOnReconnect: false } as const;

export function useBranding() {
  const { api } = useApp();
  return useBandQuery<BrandingRead>(
    ["brand-branding"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/branding", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    EDITOR_READ,
  );
}

export function useUpdateBranding() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<BrandingRead, UpdateBrandingRequest>(
    async (band, body) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/branding", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "brand-branding"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "brand-versions", "branding"] });
      },
    },
  );
}

// ---- photos ----------------------------------------------------------------------------------------------------

export function useBandPhotos() {
  const { api } = useApp();
  return useBandQuery<BandPhotosRead>(
    ["brand-photos"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/photos", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    EDITOR_READ,
  );
}

export function useUpdatePhotos() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<BandPhotosRead, PhotoSlotInput[]>(
    async (band, photos) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/photos", {
        params: { path: { bandId: band } },
        body: { photos },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "brand-photos"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "brand-versions", "photos"] });
      },
    },
  );
}

// ---- bios ------------------------------------------------------------------------------------------------------

export function useBios() {
  const { api } = useApp();
  return useBandQuery<BioRead>(
    ["brand-bios"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/bios", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    EDITOR_READ,
  );
}

export function useUpdateBios() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<BioRead, BioRead>(
    async (band, body) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/bios", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "brand-bios"] }) },
  );
}
